import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Confirm, Inform, Modal, Table, Tooltip } from '@components/common';
import {
	TSetSiteId,
} from '@components/routes/AdminPanel/AssetProfile/TelecomSites/LinkAssetToSiteForm/LinkAssetToSiteForm';

import { Icons } from '@icons/icons';

import { useLinkAndVerifySites } from '@hooks/useLinkAndVerifySite';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { isEmpty } from '@utils/common';

import { IRentInfoItem } from '@interfaces/rent/assetRent';

import { MODAL_WIDTH_580 } from '@constants/modals';
import { routes } from '@constants/routes';
import {
	siteCantBeLinkedWarning,
	siteOutOfRadiusText,
	updateCoordsAndLinkText,
	updateCoordsText,
} from '@components/routes/AdminPanel/ManageSites/constants';

import getColumns from './getColumns';
import LinkAssetToSiteForm from './LinkAssetToSiteForm';

import styles from './TelecomSites.module.scss';

export interface ITelecomSitesProps {
	assetDbId?: string;
	rentInfo?: IRentInfoItem[];
	isManageAsset: boolean;
}

const linkSiteTooltipText = 'Link telecom Site to the Asset';
const emptyText = 'No Telecom Site has been linked yet';

const { adminPanel } = routes;

function TelecomSites({
	assetDbId,
	rentInfo = [],
	isManageAsset,
}: ITelecomSitesProps) {
	const navigate = useNavigate();

	const linkedSites = isEmpty(rentInfo)
		? []
		: rentInfo?.filter((item: IRentInfoItem) => !!item.site)
			?.map((item: IRentInfoItem) => item.site);

	const {
		accessPermission: {
			networkOperatorTelecomSitesAttributeUpdater,
			networkOperatorTelecomSitesViewer,
			networkOperatorAssetEnquirer,
			assetConsumerAssetEnquirer,
		},
	} = useAppSelector(auth);

	const [isLinkFormModal, setIsLinkFormModal] = useState(false);
	const [isUnlinkConfirm, setIsUnlinkConfirm] = useState(false);
	const [isInRangeInform, setIsInRangeInform] = useState(false);
	const [isRewriteCoordConfirm, setIsRewriteCoordConfirm] = useState(false);

	const isEmptyRentInfo = isEmpty(rentInfo);
	const isLinkAccess = networkOperatorTelecomSitesAttributeUpdater
		&& (networkOperatorAssetEnquirer || assetConsumerAssetEnquirer);

	const isViewSiteAccess = networkOperatorTelecomSitesAttributeUpdater || networkOperatorTelecomSitesViewer;

	const [linkedSiteId, setLinkedSiteIdDbId] = useState<string | null>(null);
	const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
	const [isInRange, setIsInRange] = useState(false);
	const [isEditLinkMode, setIsEditLinkMode] = useState(false);

	const linkedSite = linkedSites[0];

	const clearLinkAction = () => {
		setLinkedSiteIdDbId(null);
		setIsEditLinkMode(false);
		setIsInRange(false);
	};

	const closeUnlinkConfirm = () => {
		setIsUnlinkConfirm(false);
	};

	const toggleLinkModal = () => setIsLinkFormModal(prevState => !prevState);

	const closeLinkModal = () => {
		setIsLinkFormModal(false);
		setIsEditLinkMode(false);
		clearLinkAction();
	};

	const closeUpdateSiteModal = () => setIsRewriteCoordConfirm(false);

	const toggleUpdateSiteModal = () => setIsRewriteCoordConfirm(prevState => !prevState);

	const toggleInRangeInform = () => setIsInRangeInform(prevState => !prevState);

	const {
		linkSiteHandler,
		unlinkSiteHandler,
		relinkSiteHandler,
		updateAndLinkSiteHandler,
		updateAndRelinkSiteHandler,
		updateLinkedSitesHandler,
		checkLinkedSitesHandler,
		inRadiusStatuses,
		inRadiusStatusesLoading,
	} = useLinkAndVerifySites({
		closeUnlinkConfirm,
		closeLinkModal,
		closeUpdateSiteModal,
		toggleUpdateSiteModal,
		toggleInRangeInform,
		isLinkAccess,
		setIsEditLinkMode,
	});

	const writeHandler = useCallback(() => {
		if (assetDbId && linkedSiteId) {
			updateAndLinkSiteHandler({ assetDbId, siteDbId: linkedSiteId });
		}
	}, [assetDbId, linkedSiteId, updateAndLinkSiteHandler]);

	const linkHandler = useCallback(async (siteDbId: string) => {
		if (!assetDbId) return;

		linkSiteHandler({ assetDbId, siteDbId });
		setLinkedSiteIdDbId(siteDbId);
	}, [
		assetDbId,
		linkSiteHandler,
	]);

	useEffect(() => {
		if (inRadiusStatuses[0]) setIsInRange(inRadiusStatuses[0].isInRadius);
	}, [inRadiusStatuses]);

	const checkAlreadyLinkedSitesCoords = useCallback(() => {
		if (linkedSite?.id) checkLinkedSitesHandler([linkedSite.id]);
	}, [checkLinkedSitesHandler, linkedSite?.id]);

	const handleUnlinkItems = () => unlinkSiteHandler(linkedSite?.id);

	const rewriteLinkedSitesCoord = useCallback(() => {
		if (isEditLinkMode && linkedSite?.id && selectedSiteId && assetDbId) {
			updateAndRelinkSiteHandler({
				assetDbId,
				siteDbId: linkedSite?.id,
				newSiteId: selectedSiteId,
			});
		} else if (!isInRange && linkedSiteId) {
			updateLinkedSitesHandler([linkedSiteId]);
		}
	}, [
		assetDbId,
		isEditLinkMode,
		isInRange,
		linkedSite?.id,
		linkedSiteId,
		selectedSiteId,
		updateAndRelinkSiteHandler,
		updateLinkedSitesHandler,
	]);

	const changeLinkHandler = useCallback((newSiteId: string) => {
		setSelectedSiteId(newSiteId);

		if (assetDbId && linkedSiteId) {
			relinkSiteHandler({
				assetDbId,
				siteDbId: linkedSiteId,
				newSiteId: newSiteId,
			});
		}
	}, [
		assetDbId,
		linkedSiteId,
		relinkSiteHandler,
	]);

	const closeInRangeInform = () => {
		setIsInRangeInform(false);
		clearLinkAction();
	};

	const handleOpenSiteProfile = useCallback(() => {
		if (linkedSite?.id) {
			navigate(`${adminPanel.index}${adminPanel.manageSite}/site-profile/${linkedSite?.id}`);
		}
	}, [linkedSite?.id, navigate]);

	const handleUnlinkSite = useCallback(() => {
		setIsUnlinkConfirm(true);
	}, []);

	const columns = useMemo(() => {
		return getColumns({
			handleUnlinkSite,
			handleVerifyCoordinates: checkAlreadyLinkedSitesCoords,
			handleOpenSiteProfile,
			isViewSiteAccess,
		});
	}, [
		checkAlreadyLinkedSitesCoords,
		handleOpenSiteProfile,
		handleUnlinkSite,
		isViewSiteAccess,
	]);

	const rewriteLinkedSitesHandler = useCallback(() => {
		if (linkedSite?.id) rewriteLinkedSitesCoord();
		else writeHandler();
	}, [linkedSite?.id, writeHandler, rewriteLinkedSitesCoord]);

	const submitOnLinkHandler = useCallback((siteId: string) => {
		if (isEditLinkMode) changeLinkHandler(siteId);
		else void linkHandler(siteId);
	}, [
		changeLinkHandler,
		isEditLinkMode,
		linkHandler,
	]);

	return (
		<div className={styles.telecomSites}>
			{isLinkAccess && !isManageAsset
				&& <div className={styles.header}>
					<Tooltip
						content={linkSiteTooltipText}
					>
						<div className={styles.btnContainer}>
							<Button
								disabled={isEmptyRentInfo || !!linkedSite}
								icon={Icons.link({ color: 'white' })}
								onClick={toggleLinkModal}
								text='Link site'
							/>
						</div>
					</Tooltip>
				</div>
			}
			<Table
				columns={columns}
				data={linkedSites}
				emptyText={emptyText}
				isLoading={false}
				isWithoutActions={false}
				isWithoutCheckbox
				isWithoutDeleteAction
				isWithoutFilter
			/>
			<Modal
				isOpen={isLinkFormModal}
				onClose={closeLinkModal}
				title='Link Site'
				width={MODAL_WIDTH_580}
			>
				<LinkAssetToSiteForm
					closeModal={closeLinkModal}
					isLinkAccess={true}
					setSiteId={setSelectedSiteId as TSetSiteId}
					siteId={selectedSiteId}
					submitHandler={submitOnLinkHandler}
				/>
			</Modal>

			<Modal
				isOpen={isRewriteCoordConfirm}
				onClose={closeUpdateSiteModal}
			>
				<Confirm
					negativeBtnText='Cancel'
					onClose={closeUpdateSiteModal}
					onSubmit={rewriteLinkedSitesHandler}
					positiveBtnText='Update coordinates'
					question={linkedSite?.id ? siteOutOfRadiusText : siteCantBeLinkedWarning}
				>
					{linkedSite?.id ? updateCoordsText : updateCoordsAndLinkText}
				</Confirm>
			</Modal>

			<Modal
				isOpen={isUnlinkConfirm}
				onClose={closeUnlinkConfirm}
			>
				<Confirm
					negativeBtnText='Cancel'
					onClose={closeUnlinkConfirm}
					onSubmit={handleUnlinkItems}
					positiveBtnText='Unlink'
					question='Are you sure you want to unlink this site from the Asset?'
				>
					{/* eslint-disable-next-line max-len */}
					UnLinking doesn&apos;t delete the Site from database. It will only disconnect the Site from the Asset.
				</Confirm>
			</Modal>

			<Modal
				isOpen={isInRangeInform}
				onClose={closeInRangeInform}
				title='Information'
			>
				<Inform
					informText='The Site is located within the linked Asset radius'
					isLoading={inRadiusStatusesLoading}
					onClose={closeInRangeInform}
				/>
			</Modal>
		</div>
	);
}

export default memo(TelecomSites);
